import { useState } from 'react'
import { Button } from '../ui/Button'
import { Input } from '../ui/Input'
import { Card } from '../ui/Card'
import { X, Edit, AlertTriangle } from 'lucide-react'

interface PriceOverrideModalProps {
  isOpen: boolean
  onClose: () => void
  onApply: (newPrice: number) => void
  itemName: string
  originalPrice: number
  currentPrice: number
}

export const PriceOverrideModal = ({ 
  isOpen, 
  onClose, 
  onApply, 
  itemName,
  originalPrice,
  currentPrice
}: PriceOverrideModalProps) => {
  const [newPrice, setNewPrice] = useState(currentPrice.toString())
  const [reason, setReason] = useState('')

  const handleApply = () => {
    const price = parseFloat(newPrice)
    if (price > 0) {
      onApply(price)
      setNewPrice('')
      setReason('')
      onClose()
    }
  }

  const handleQuickPrice = (multiplier: number) => {
    const price = originalPrice * multiplier
    onApply(price)
    onClose()
  }

  if (!isOpen) return null

  const priceChange = parseFloat(newPrice) - originalPrice
  const priceChangePercentage = ((priceChange / originalPrice) * 100).toFixed(1)

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-md bg-white dark:bg-gray-800">
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Override Price
          </h3>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="p-4 space-y-4">
          {/* Item Info */}
          <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
            <h4 className="font-medium text-gray-900 dark:text-gray-100 text-sm">
              {itemName}
            </h4>
            <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mt-1">
              <span>Original Price:</span>
              <span>₹{originalPrice.toFixed(2)}</span>
            </div>
            {currentPrice !== originalPrice && (
              <div className="flex justify-between text-sm text-blue-600 dark:text-blue-400">
                <span>Current Price:</span>
                <span>₹{currentPrice.toFixed(2)}</span>
              </div>
            )}
          </div>

          {/* Quick Price Options */}
          <div className="space-y-3">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Quick Price Adjustments
            </label>
            <div className="grid grid-cols-2 gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleQuickPrice(0.9)}
                className="text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20"
              >
                10% Less
                <span className="text-xs ml-1">(₹{(originalPrice * 0.9).toFixed(2)})</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleQuickPrice(0.8)}
                className="text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20"
              >
                20% Less
                <span className="text-xs ml-1">(₹{(originalPrice * 0.8).toFixed(2)})</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleQuickPrice(1.1)}
                className="text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-900/20"
              >
                10% More
                <span className="text-xs ml-1">(₹{(originalPrice * 1.1).toFixed(2)})</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleQuickPrice(1.2)}
                className="text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-900/20"
              >
                20% More
                <span className="text-xs ml-1">(₹{(originalPrice * 1.2).toFixed(2)})</span>
              </Button>
            </div>
          </div>

          <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Custom Price
            </label>
          </div>

          {/* Custom Price Input */}
          <div className="space-y-2">
            <div className="relative">
              <Input
                type="number"
                min="0"
                step="0.01"
                value={newPrice}
                onChange={(e) => setNewPrice(e.target.value)}
                placeholder="Enter new price"
                className="text-right pr-8"
              />
              <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">
                ₹
              </span>
            </div>
            
            {/* Price Change Indicator */}
            {newPrice && parseFloat(newPrice) !== originalPrice && (
              <div className={`text-xs flex items-center gap-1 ${
                priceChange > 0 ? 'text-orange-600' : 'text-green-600'
              }`}>
                {priceChange > 0 ? (
                  <span>↗ Price increase: +₹{priceChange.toFixed(2)} (+{priceChangePercentage}%)</span>
                ) : (
                  <span>↘ Price reduction: ₹{Math.abs(priceChange).toFixed(2)} ({priceChangePercentage}%)</span>
                )}
              </div>
            )}

            {/* Warning for significant price changes */}
            {Math.abs(parseFloat(priceChangePercentage)) > 50 && (
              <div className="flex items-center gap-2 p-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded text-yellow-800 dark:text-yellow-200 text-xs">
                <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                <span>Large price change detected. This may require manager approval.</span>
              </div>
            )}
          </div>

          {/* Reason for Override */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Reason (Optional)
            </label>
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g., Price match, Bulk discount, Damaged item"
              className="text-sm"
            />
          </div>

          {/* Preview */}
          {newPrice && parseFloat(newPrice) > 0 && (
            <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg text-sm">
              <div className="flex justify-between">
                <span>Original Price:</span>
                <span className={parseFloat(newPrice) !== originalPrice ? 'line-through text-gray-400' : ''}>
                  ₹{originalPrice.toFixed(2)}
                </span>
              </div>
              {parseFloat(newPrice) !== originalPrice && (
                <div className="flex justify-between font-semibold text-blue-600 dark:text-blue-400">
                  <span>New Price:</span>
                  <span>₹{parseFloat(newPrice).toFixed(2)}</span>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex gap-2 p-4 border-t border-gray-200 dark:border-gray-700">
          <Button variant="outline" onClick={onClose} className="flex-1">
            Cancel
          </Button>
          <Button 
            onClick={handleApply} 
            disabled={!newPrice || parseFloat(newPrice) <= 0}
            className="flex-1"
          >
            <Edit className="h-4 w-4 mr-1" />
            Override Price
          </Button>
        </div>
      </Card>
    </div>
  )
}